import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  limit as firestoreLimit,
  addDoc,
} from 'firebase/firestore';
import type { CreateOrderDto } from '../../application/dtos/OrderDto';
import { IOrderRepository } from '../../domain/repositories/IOrderRepository';
import { getFirebaseDb, ORDERS_AGGREGATE_COLLECTION } from '../../../lib/firebase';
import { OrderAggregateDocument } from '@/types/order-aggregate.types';

/**
 * Firebase implementation of the Order Repository
 * Handles all order data operations with Firebase Firestore
 */
export class FirebaseOrderRepository implements IOrderRepository {
  // Order CRUD operations
  async findOrderById(orderId: string): Promise<OrderAggregateDocument | null> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, ORDERS_AGGREGATE_COLLECTION, orderId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return { id: docSnap.id, ...docSnap.data() } as OrderAggregateDocument;
    } catch (error) {
      console.error('Error finding order by ID:', error);
      throw error;
    }
  }

  async findAllOrders(limit?: number): Promise<OrderAggregateDocument[]> {
    try {
      const db = await getFirebaseDb();
      let q = query(collection(db, ORDERS_AGGREGATE_COLLECTION), orderBy('timestamp', 'desc'));

      if (limit) {
        q = query(q, firestoreLimit(limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as OrderAggregateDocument,
      );
    } catch (error) {
      console.error('Error finding all orders:', error);
      throw error;
    }
  }

  async findOrdersByStatus(status: string, limit?: number): Promise<OrderAggregateDocument[]> {
    try {
      const db = await getFirebaseDb();
      let q = query(
        collection(db, ORDERS_AGGREGATE_COLLECTION),
        where('status', '==', status),
        orderBy('timestamp', 'desc'),
      );

      if (limit) {
        q = query(q, firestoreLimit(limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as OrderAggregateDocument,
      );
    } catch (error) {
      console.error('Error finding orders by status:', error);
      throw error;
    }
  }

  async findOrdersByUserId(userId: string): Promise<OrderAggregateDocument[]> {
    try {
      const db = await getFirebaseDb();
      const q = query(
        collection(db, ORDERS_AGGREGATE_COLLECTION),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as OrderAggregateDocument,
      );
    } catch (error) {
      console.error('Error finding orders by user ID:', error);
      throw error;
    }
  }

  async saveOrder(order: OrderAggregateDocument): Promise<string> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(collection(db, ORDERS_AGGREGATE_COLLECTION), order.id);
      await setDoc(docRef, order);
      return docRef.id;
    } catch (error) {
      console.error('Error saving order:', error);
      throw error;
    }
  }

  async updateOrder(_orderId: string, updates: Partial<OrderAggregateDocument>): Promise<void> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, ORDERS_AGGREGATE_COLLECTION, _orderId);
      await updateDoc(docRef, {
        ...updates,
        updated_at: Date.now(),
      });
    } catch (error) {
      console.error('Error updating order:', error);
      throw error;
    }
  }

  async deleteOrder(orderId: string): Promise<void> {
    try {
      const db = await getFirebaseDb();
      const docRef = doc(db, ORDERS_AGGREGATE_COLLECTION, orderId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting order:', error);
      throw error;
    }
  }

  // Order submission
  async submitOrder(orderData: CreateOrderDto): Promise<{
    orderId: string;
    timestamp: number;
  }> {
    try {
      const db = await getFirebaseDb();
      const timestamp = Date.now();

      // Convert CreateOrderDto to OrderAggregateDocument format
      const adaptedCart = orderData.items.map((item) => ({
        variant_id: item.variantId,
        sku: item.sku,
        quantity: item.quantity,
        note: item.note,
        in_cart: {
          added_at: timestamp,
          price: item.price,
          selected_attributes: item.attributes,
        },
      }));

      const orderDoc: Omit<OrderAggregateDocument, 'id' | 'aggregatedAt' | 'cart_subtotal'> = {
        cart: adaptedCart as OrderAggregateDocument['cart'],
        deliveryData: {
          fullName: orderData.deliveryInfo.fullName,
          phoneNumber: orderData.deliveryInfo.phoneNumber,
          email: orderData.deliveryInfo.email,
          contactMethod: orderData.deliveryInfo.contactMethod,
          streetAddress: orderData.deliveryInfo.streetAddress,
          city: orderData.deliveryInfo.city,
          state: orderData.deliveryInfo.state,
          zipCode: orderData.deliveryInfo.zipCode,
          deliveryDate: orderData.deliveryInfo.deliveryDate,
          deliveryTime: orderData.deliveryInfo.deliveryTime,
        } as OrderAggregateDocument['deliveryData'],
        dietaryData: orderData.dietaryRequirements as OrderAggregateDocument['dietaryData'],
        status: 'pending',
        timestamp,
        paid: false,
        stripe_payment_status: 'pending',
      };

      const docRef = await addDoc(collection(db, ORDERS_AGGREGATE_COLLECTION), orderDoc);

      return {
        orderId: docRef.id,
        timestamp,
      };
    } catch (error) {
      console.error('Error submitting order:', error);
      throw error;
    }
  }

  // Order status management
  async updateOrderStatus(orderId: string, status: string): Promise<void> {
    await this.updateOrder(orderId, { status: status as OrderAggregateDocument['status'] });
  }

  async markOrderAsPaid(orderId: string): Promise<void> {
    await this.updateOrder(orderId, {
      paid: true,
      stripe_payment_status: 'succeeded',
      status: 'confirmed',
    } as Partial<OrderAggregateDocument>);
  }

  async markOrderAsDelivered(orderId: string): Promise<void> {
    await this.updateOrder(orderId, {
      status: 'completed',
    } as Partial<OrderAggregateDocument>);
  }

  async cancelOrder(orderId: string, _reason?: string): Promise<void> {
    await this.updateOrder(orderId, {
      status: 'cancelled',
    } as Partial<OrderAggregateDocument>);
  }

  // Subscription operations
  subscribeToOrders(callback: (orders: OrderAggregateDocument[]) => void, limit?: number): () => void {
    try {
      const setup = async () => {
        const db = await getFirebaseDb();
        let q = query(collection(db, ORDERS_AGGREGATE_COLLECTION), orderBy('timestamp', 'desc'));

        if (limit) {
          q = query(q, firestoreLimit(limit));
        }

        return onSnapshot(
          q,
          (snapshot) => {
            const orders = snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                }) as OrderAggregateDocument,
            );
            callback(orders);
          },
          (error) => {
            console.error('Error in orders subscription:', error);
          },
        );
      };

      let unsubscribe: (() => void) | null = null;
      setup()
        .then((unsub) => {
          unsubscribe = unsub;
        })
        .catch((error) => {
          console.error('Error setting up orders subscription:', error);
        });
      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up orders subscription:', error);
      return () => {};
    }
  }

  subscribeToOrdersByStatus(
    status: string,
    callback: (orders: OrderAggregateDocument[]) => void,
    limit?: number,
  ): () => void {
    try {
      const setup = async () => {
        const db = await getFirebaseDb();
        let q = query(
          collection(db, ORDERS_AGGREGATE_COLLECTION),
          where('status', '==', status),
          orderBy('timestamp', 'desc'),
        );

        if (limit) {
          q = query(q, firestoreLimit(limit));
        }

        return onSnapshot(
          q,
          (snapshot) => {
            const orders = snapshot.docs.map(
              (doc) =>
                ({
                  id: doc.id,
                  ...doc.data(),
                }) as OrderAggregateDocument,
            );
            callback(orders);
          },
          (error) => {
            console.error('Error in orders by status subscription:', error);
          },
        );
      };

      let unsubscribe: (() => void) | null = null;
      setup()
        .then((unsub) => {
          unsubscribe = unsub;
        })
        .catch((error) => {
          console.error('Error setting up orders by status subscription:', error);
        });
      return () => {
        if (unsubscribe) unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up orders by status subscription:', error);
      return () => {};
    }
  }

  // Analytics and reporting
  async getOrderStats(): Promise<{
    totalOrders: number;
    pendingOrders: number;
    completedOrders: number;
    totalRevenue: number;
  }> {
    try {
      const allOrders = await this.findAllOrders();

      const totalOrders = allOrders.length;
      const pendingOrders = allOrders.filter(
        (order) => order.status === 'pending' || order.status === 'confirmed',
      ).length;
      const completedOrders = allOrders.filter((order) => order.status === 'completed').length;

      // Calculate total revenue from completed orders
      const totalRevenue = allOrders
        .filter((order) => order.status === 'completed' && order.paid)
        .reduce((total, _order) => {
          // This would need to be calculated based on cart items and their prices
          // For now, returning 0 as we'd need product pricing data
          return total;
        }, 0);

      return {
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue,
      };
    } catch (error) {
      console.error('Error getting order stats:', error);
      throw error;
    }
  }

  async getOrdersByDateRange(startDate: Date, endDate: Date): Promise<OrderAggregateDocument[]> {
    try {
      const db = await getFirebaseDb();
      const q = query(
        collection(db, ORDERS_AGGREGATE_COLLECTION),
        where('timestamp', '>=', startDate.getTime()),
        where('timestamp', '<=', endDate.getTime()),
        orderBy('timestamp', 'desc'),
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as OrderAggregateDocument,
      );
    } catch (error) {
      console.error('Error getting orders by date range:', error);
      throw error;
    }
  }
}
